import * as Tone from 'tone';
import { BaseVisualizer } from './base-visualizer';

/**
 * Layer state interface for external control
 */
export interface LayerVisualState {
  type: string;
  muted: boolean;
  volume: number; // 0-1
}

/**
 * Unified Grid Visualizer - Sequencer grid with waveform overlay and layer triggers
 * Clean visualization focusing on the sequencer and waveform
 */
export class UnifiedGridVisualizer extends BaseVisualizer {
  private analyser: Tone.Analyser | null = null;
  private smoothedWaveform: Float32Array = new Float32Array(256);
  private waveformSmoothing = 0.25;
  
  // Grid configuration
  private gridColumns = 16;
  
  // Layer visual states (muted, volume) from external control
  private layerVisualStates: LayerVisualState[] = [
    { type: 'percussion', muted: false, volume: 0.25 },
    { type: 'bass', muted: false, volume: 0.25 },
    { type: 'melody', muted: false, volume: 0.25 },
    { type: 'pad', muted: false, volume: 0.25 },
  ];
  
  // Layer state for triggers
  private layerLevels: Map<string, number> = new Map([
    ['percussion', 0],
    ['bass', 0],
    ['melody', 0],
    ['pad', 0],
  ]);
  private layerDecay = 0.92;
  
  // Ripple effects for each layer
  private layerRipples: Map<string, { x: number; y: number; radius: number; opacity: number }[]> = new Map([
    ['percussion', []],
    ['bass', []],
    ['melody', []],
    ['pad', []],
  ]);
  
  // Step sequencer position
  private currentStep = 0;
  private stepTriggers: Map<string, number[]> = new Map([
    ['percussion', []],
    ['bass', []],
    ['melody', []],
    ['pad', []],
  ]);
  
  // Event listener references for cleanup
  private grainTriggerHandler: EventListener | null = null;
  private transportStepHandler: EventListener | null = null;

  init(containerId: string): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    this.createCanvas(container);
    
    // Waveform analyzer
    this.analyser = new Tone.Analyser('waveform', 512);
    Tone.getDestination().connect(this.analyser);

    this.resize();
    window.addEventListener('resize', () => this.resize());
    
    // Listen for grain triggers
    this.grainTriggerHandler = ((event: CustomEvent) => {
      const { type } = event.detail;
      this.trigger(type);
    }) as EventListener;
    window.addEventListener('grain:trigger', this.grainTriggerHandler);
    
    // Listen for transport step events to stay in sync
    this.transportStepHandler = ((event: CustomEvent) => {
      const { step } = event.detail;
      this.currentStep = step;
      
      // Clear old triggers when loop restarts
      if (step === 0) {
        this.stepTriggers.forEach((_, key) => {
          this.stepTriggers.set(key, []);
        });
      }
    }) as EventListener;
    window.addEventListener('transport:step', this.transportStepHandler);

    this.drawIdle();
  }

  trigger(layer: string): void {
    // Don't record triggers for muted layers
    const layerState = this.layerVisualStates.find(s => s.type === layer);
    if (layerState?.muted) return;
    
    this.layerLevels.set(layer, 1);
    
    // Record step trigger for visual grid
    const triggers = this.stepTriggers.get(layer) || [];
    if (!triggers.includes(this.currentStep)) {
      triggers.push(this.currentStep);
      if (triggers.length > this.gridColumns * 2) {
        triggers.shift();
      }
      this.stepTriggers.set(layer, triggers);
    }
    
    // Add ripple effect at trigger position
    const { width, height } = this.getCanvasSize();
    const cellWidth = width / this.gridColumns;
    const allLayers = this.layerVisualStates;
    const layerIndex = allLayers.findIndex(l => l.type === layer);
    const verticalPadding = height * 0.15;
    const availableHeight = height - verticalPadding * 2;
    const layerSpacing = allLayers.length > 1 ? availableHeight / (allLayers.length - 1) : 0;
    const y = allLayers.length === 1 ? height / 2 : verticalPadding + layerIndex * layerSpacing;
    const x = this.currentStep * cellWidth + cellWidth / 2;
    
    const ripples = this.layerRipples.get(layer) || [];
    ripples.push({ x, y, radius: 0, opacity: 0.8 });
    if (ripples.length > 6) ripples.shift();
    this.layerRipples.set(layer, ripples);
  }

  // Update layer visual states from external control
  updateLayerStates(states: LayerVisualState[]): void {
    this.layerVisualStates = states;
  }

  private drawIdle(): void {
    if (!this.ctx) return;
    const { width, height } = this.getCanvasSize();

    // Solid background
    this.ctx.fillStyle = this.colors.background;
    this.ctx.fillRect(0, 0, width, height);

    // Draw faint grid
    this.drawGrid(width, height, 0.08);
    
    // Draw center waveform line
    const centerY = height / 2;
    this.ctx.strokeStyle = this.applyOpacity(this.colors.secondary, 0.2);
    this.ctx.lineWidth = 1;
    this.ctx.beginPath();
    this.ctx.moveTo(0, centerY);
    this.ctx.lineTo(width, centerY);
    this.ctx.stroke();
  }

  private drawGrid(width: number, height: number, opacity: number = 0.3): void {
    if (!this.ctx) return;
    
    const cellWidth = width / this.gridColumns;
    
    // Vertical grid lines
    for (let i = 0; i <= this.gridColumns; i++) {
      const x = i * cellWidth;
      const isBeat = i % 4 === 0;
      const lineOpacity = isBeat ? opacity * 1.5 : opacity;
      
      this.ctx.strokeStyle = this.applyOpacity(this.colors.secondary, lineOpacity);
      this.ctx.lineWidth = isBeat ? 1.5 : 1;
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }
  }

  protected draw(): void {
    if (!this.ctx || !this.analyser) return;
    const { width, height } = this.getCanvasSize();

    // Clear with solid background
    this.ctx.fillStyle = this.colors.background;
    this.ctx.fillRect(0, 0, width, height);
    
    // Draw vertical grid lines (background)
    this.drawGrid(width, height, 0.06);
    
    // Draw waveform in center
    this.drawWaveformOverlay(width, height);
    
    // Draw ripple effects (behind layer shapes)
    this.drawRipples(width, height);
    
    // Draw layer trigger shapes
    this.drawLayerShapes(width, height);
    
    // Draw playhead line
    this.drawPlayhead(width, height);
    
    // Decay layer levels
    this.layerLevels.forEach((level, key) => {
      this.layerLevels.set(key, level * this.layerDecay);
    });
    
    // Update ripples
    this.layerRipples.forEach((ripples, layer) => {
      const updated = ripples
        .map(r => ({ ...r, radius: r.radius + 4, opacity: r.opacity * 0.92 }))
        .filter(r => r.opacity > 0.01);
      this.layerRipples.set(layer, updated);
    });
  }

  private drawWaveformOverlay(width: number, height: number): void {
    if (!this.analyser || !this.ctx) return;
    
    const centerY = height / 2;
    const amplitude = height * 0.35;
    const gain = 3.0;
    
    const waveform = this.analyser.getValue() as Float32Array;
    
    // Resize smoothed array if needed
    if (this.smoothedWaveform.length !== waveform.length) {
      this.smoothedWaveform = new Float32Array(waveform.length);
    }

    // Apply smoothing with gain
    for (let i = 0; i < waveform.length; i++) {
      const boosted = waveform[i] * gain;
      const clamped = Math.max(-1, Math.min(1, boosted));
      this.smoothedWaveform[i] =
        this.smoothedWaveform[i] * this.waveformSmoothing +
        clamped * (1 - this.waveformSmoothing);
    }

    const sliceWidth = width / this.smoothedWaveform.length;

    // Draw waveform as filled area
    this.ctx.fillStyle = this.applyOpacity(this.colors.primary, 0.08);
    this.ctx.beginPath();
    this.ctx.moveTo(0, centerY);
    
    for (let i = 0; i < this.smoothedWaveform.length; i++) {
      const x = i * sliceWidth;
      const y = centerY + this.smoothedWaveform[i] * amplitude;
      this.ctx.lineTo(x, y);
    }
    this.ctx.lineTo(width, centerY);
    this.ctx.closePath();
    this.ctx.fill();
    
    // Draw main waveform line
    this.ctx.strokeStyle = this.colors.primary;
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.beginPath();

    for (let i = 0; i < this.smoothedWaveform.length; i++) {
      const x = i * sliceWidth;
      const y = centerY + this.smoothedWaveform[i] * amplitude;
      if (i === 0) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
  }

  private drawRipples(_width: number, _height: number): void {
    if (!this.ctx) return;
    
    this.layerRipples.forEach((ripples) => {
      ripples.forEach(ripple => {
        this.ctx!.strokeStyle = this.applyOpacity(this.colors.primary, ripple.opacity * 0.3);
        this.ctx!.lineWidth = 1;
        this.ctx!.beginPath();
        this.ctx!.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        this.ctx!.stroke();
      });
    });
  }

  private drawLayerShapes(width: number, height: number): void {
    if (!this.ctx) return;
    
    const allLayers = this.layerVisualStates;
    if (allLayers.length === 0) return;
    
    const cellWidth = width / this.gridColumns;
    
    // Distribute layers vertically
    const verticalPadding = height * 0.15;
    const availableHeight = height - verticalPadding * 2;
    const layerSpacing = allLayers.length > 1 ? availableHeight / (allLayers.length - 1) : 0;
    
    const baseRadius = Math.min(cellWidth, height / 6) * 0.35;
    
    allLayers.forEach((layerState, layerIndex) => {
      const layer = layerState.type;
      const level = this.layerLevels.get(layer) || 0;
      const isMuted = layerState.muted;
      
      const y = allLayers.length === 1 
        ? height / 2 
        : verticalPadding + layerIndex * layerSpacing;
      
      const volumeScale = 0.5 + layerState.volume * 1.2;
      const circleRadius = baseRadius * volumeScale;
      
      for (let col = 0; col < this.gridColumns; col++) {
        const x = col * cellWidth + cellWidth / 2;
        const isCurrentStep = col === this.currentStep;
        const wasTriggered = (this.stepTriggers.get(layer) || []).includes(col);
        
        // Draw outer ring
        const ringOpacity = isMuted 
          ? (isCurrentStep ? 0.4 : 0.25)
          : (isCurrentStep ? 0.5 : 0.2);
        
        this.ctx!.strokeStyle = this.applyOpacity(
          isMuted ? this.colors.secondary : this.colors.primary, 
          ringOpacity
        );
        this.ctx!.lineWidth = isCurrentStep ? 2 : 1.5;
        this.ctx!.beginPath();
        this.ctx!.arc(x, y, circleRadius, 0, Math.PI * 2);
        this.ctx!.stroke();
        
        // Skip fills for muted layers
        if (isMuted) continue;
        
        // Draw filled circle if triggered
        if (wasTriggered || (isCurrentStep && level > 0.1)) {
          const fillLevel = isCurrentStep 
            ? Math.max(level, wasTriggered ? 0.7 : 0) 
            : (wasTriggered ? 0.35 : 0);
          
          if (fillLevel > 0) {
            const activeRadius = circleRadius * (0.85 + level * 0.15);
            this.ctx!.fillStyle = this.applyOpacity(this.colors.primary, fillLevel * 0.85);
            this.ctx!.beginPath();
            this.ctx!.arc(x, y, activeRadius, 0, Math.PI * 2);
            this.ctx!.fill();
          }
        }
      }
    });
  }

  private drawPlayhead(width: number, height: number): void {
    if (!this.ctx) return;
    
    const cellWidth = width / this.gridColumns;
    const x = this.currentStep * cellWidth + cellWidth / 2;
    
    // Vertical playhead line
    this.ctx.strokeStyle = this.applyOpacity(this.colors.primary, 0.4);
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(x, 0);
    this.ctx.lineTo(x, height);
    this.ctx.stroke();
    
    // Top and bottom accent dots
    this.ctx.fillStyle = this.applyOpacity(this.colors.primary, 0.8);
    this.ctx.beginPath();
    this.ctx.arc(x, 4, 3, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.beginPath();
    this.ctx.arc(x, height - 4, 3, 0, Math.PI * 2);
    this.ctx.fill();
  }

  stop(): void {
    super.stop();
    this.currentStep = 0;
    this.stepTriggers.forEach((_, key) => {
      this.stepTriggers.set(key, []);
    });
    this.layerLevels.forEach((_, key) => this.layerLevels.set(key, 0));
    this.layerRipples.forEach((_, key) => this.layerRipples.set(key, []));
    this.smoothedWaveform.fill(0);
    this.drawIdle();
  }

  dispose(): void {
    // Remove event listeners
    if (this.grainTriggerHandler) {
      window.removeEventListener('grain:trigger', this.grainTriggerHandler);
      this.grainTriggerHandler = null;
    }
    if (this.transportStepHandler) {
      window.removeEventListener('transport:step', this.transportStepHandler);
      this.transportStepHandler = null;
    }
    
    if (this.analyser) {
      this.analyser.dispose();
      this.analyser = null;
    }
    super.dispose();
  }
}
